import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { getProjects, preloadPortfolioData } from '../firebase';
import { useLoading } from './LoadingContext';
import { logError } from '../utils/error-handler';

const ProjectsContext = createContext(null);

export const ProjectsProvider = ({ children }) => {
  const [projects, setProjects] = useState(getProjects() || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { finishTask, beginExpectedTasks } = useLoading();

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    beginExpectedTasks(['projects']);

    try {
      await preloadPortfolioData();
      const projectsData = getProjects();
      setProjects(projectsData || []);
    } catch (err) {
      logError(err, 'ProjectsProvider', {});
      setError(err);
    } finally {
      setLoading(false);
      finishTask('projects');
    }
  }, [beginExpectedTasks, finishTask]);

  const switchLanguageClientSide = useCallback(() => {
    beginExpectedTasks(['projects']);

    requestAnimationFrame(() => {
      try {
        const projectsData = getProjects();
        setProjects(projectsData || []);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Projects', {});
        loadProjects();
      } finally {
        finishTask('projects');
      }
    });
  }, [beginExpectedTasks, finishTask, loadProjects]);

  useEffect(() => {
    let isMounted = true;
    loadProjects();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadProjects, switchLanguageClientSide]);

  const value = useMemo(
    () => ({ projects, loading, error }),
    [projects, loading, error]
  );

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects must be used within ProjectsProvider');
  }
  return context;
};
